import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import {
  Row,
  Col,
  Typography,
  Card,
  Carousel,
  Divider,
  Grid,
  Input,
  Button,
  Image,
  notification,
} from "antd";
import { MailOutlined, PhoneOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";

import Layout from "./Layout";

const { Title, Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;

const COLORS = {
  accent: "#c9b37e",
  accentDark: "#b3a068",
};

/* ================= BASE ARROW STYLE ================= */
const ArrowStyle = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 20,
  background: "rgba(0,0,0,0.6)",
  color: "#fff",
  border: "none",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "all 0.3s ease",
  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
};

/* ================= PRODUCT CARD ================= */
const ProductCard = ({ product }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: "relative",
        borderRadius: 16,
        overflow: "hidden",
        height: "500px",
        boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
        background: "#000",
        cursor: "pointer",
      }}
    >
      <Carousel autoplay autoplaySpeed={2500} dots={false} effect="fade">
        {product.images.map((img, idx) => (
          <div key={idx}>
            <div style={{ height: "500px", width: "100%", position: "relative" }}>
              <img
                src={`/${img}.jpg`}
                alt={product.category}
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "50%", background: "linear-gradient(to top, rgba(0,0,0,0.9), transparent)" }} />
            </div>
          </div>
        ))}
      </Carousel>

      {/* Non-hover Title */}
      <div style={{ position: "absolute", bottom: 30, left: 0, right: 0, textAlign: "center", zIndex: 2, opacity: isHovered ? 0 : 1, transition: "opacity 0.3s" }}>
        <Title level={3} style={{ color: "#fff", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 0, textShadow: "0 2px 8px rgba(0,0,0,0.9)" }}>
          {product.category}
        </Title>
      </div>

      {/* Hover White Overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(255,255,255,0.97)",
          zIndex: 10,
          opacity: isHovered ? 1 : 0,
          transition: "opacity 0.4s ease",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "40px 30px",
          overflowY: "auto",
        }}
      >
        <Title level={3} style={{ color: COLORS.accentDark, margin: "0 0 16px" }}>{product.category}</Title>
        <Paragraph style={{ fontSize: 15.5, color: "#333", lineHeight: "1.65" }}>{product.description}</Paragraph>

        {product.poles && (
          <div style={{ background: "#f8f8f8", padding: "12px", borderRadius: 8, margin: "16px 0", fontSize: 13.5, color: "#555" }}>
            <strong>Note:</strong> {product.poles}
          </div>
        )}

        <Text strong style={{ color: COLORS.accentDark, fontSize: 16, marginBottom: 10 }}>Key Features:</Text>
        <ul style={{ paddingLeft: 22, margin: 0, color: "#444" }}>
          {product.features.map((f, i) => (
            <li key={i} style={{ marginBottom: 6, fontSize: 14.5 }}>{f}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

function Home() {
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const isMobileOrTablet = !screens.xl;

  const galleryRef = useRef(null);

  const onFinish = () =>
    notification.success({
      message: "Message Sent",
      description: "Our team will contact you shortly.",
    });

  const ContactForm = () => (
    <Card
      title="Contact Us"
      styles={{ header: { textAlign: "center", color: COLORS.accent, fontWeight: 700 } }}
      style={{ maxWidth: 420, margin: "0 auto", borderRadius: 16, boxShadow: "0 12px 40px rgba(0,0,0,0.12)" }}
    >
      <form action="https://formsubmit.co/contactus@jphollandwoven.com" method="POST" onSubmit={onFinish}>
        <Input name="name" size="large" placeholder="Your Name" required />
        <br /><br />
        <Input name="email" size="large" placeholder="Email Address" prefix={<MailOutlined />} required />
        <br /><br />
        <Input name="mobile" size="large" placeholder="Mobile Number" prefix={<PhoneOutlined />} required />
        <br /><br />
        <Input.TextArea name="message" rows={3} placeholder="Your Message" />
        <br /><br />
        <input type="hidden" name="_captcha" value="false" />
        <input type="hidden" name="_template" value="table" />

        <Button
          htmlType="submit"
          block
          size="large"
          style={{ height: 52, background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.accentDark})`, border: "none", fontWeight: 700, fontSize: 16 }}
        >
          SEND MESSAGE
        </Button>
      </form>
    </Card>
  );

  const products = [
    { category: "A Frames", description: "We offer a wide selection of A Frames for storing granite, stone, and other types of slabs. Our A Frames are a perfect fit for fabrication shops and slab yards.", features: ["Heavy duty material for maximum strength.", "UV Resistant paint for higher reusability.", "Moulded and Bolted design for easy transport."], images: ["A1", "A2", "A3"] },
    { category: "Bundle Racks", description: "Our bundle racks optimize storage efficiency. Ideal for distributors stocking large bundles of granite and marble. Posts can be adjusted horizontally as needed.", features: ["Rectangular poles of 60x60MM.", "MIG Welding ensures smooth movement.", "Galvanized posts topped with heavy duty Wheels."], poles: "First in industry to introduce wheels on top of poles.", images: ["R1", "R2", "R3"] },
    { category: "Display Racks", description: "Heavy-duty foldable display racks for showcasing stone samples, tiles, and landscaping material. Designed for premium outlook and durability.", features: ["Heavy duty design for high weight capacity.", "Slanted angle for better display visibility.", "UV Resistant Paint."], images: ["D1", "D2", "D3"] },
  ];

  const galleryImages = ["t1", "t2", "t3", "t4", "t5", "t6", "t7", "t8"];
  const testimonials = [
    { text: "Exceptional quality and craftsmanship. The A Frames have transformed our storage efficiency.", name: "Rajesh Kumar" },
    { text: "Timely delivery and robust products. Highly recommend JP Hollandwoven.", name: "Priya Sharma" },
    { text: "Outstanding customer service and durable bundle racks.", name: "Amit Patel" },
  ];

  // Responsive Arrow Styles
  const arrowSize = isMobile ? 38 : 50;
  const arrowFont = isMobile ? 17 : 22;
  const arrowPos = isMobile ? 12 : 24;

  const leftArrowStyle = { ...ArrowStyle, left: arrowPos, width: arrowSize, height: arrowSize, fontSize: arrowFont };
  const rightArrowStyle = { ...ArrowStyle, right: arrowPos, width: arrowSize, height: arrowSize, fontSize: arrowFont };

  return (
    <Layout>
      {/* ================= HERO ================= */}
      <section id="home" style={{ height: "100vh", position: "relative", paddingTop: 138 }}>
        <video src="/factoryvideo.mp4" autoPlay muted loop playsInline style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
        <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)" }} />

        <Row style={{ height: "100%", position: "relative", zIndex: 2 }} align="middle">
          {!isMobile && (
            <Col lg={8} style={{ paddingLeft: 60 }}>
              <ContactForm />
            </Col>
          )}
          <Col xs={24} lg={16} style={{ textAlign: "center", color: "#fff" }}>
            <Paragraph style={{ color: COLORS.accent, fontSize: isMobile ? 36 : 62, fontWeight: 700  }}>Masters of Bespoke Fabrication</Paragraph>
          </Col>
        </Row>
      </section>

      {/* Mobile Contact Form */}
      {isMobile && (
        <section style={{ padding: "40px 20px", background: "#f8f8f8" }}>
          <ContactForm />
        </section>
      )}

      <div style={{ padding: screens.md ? "80px 60px" : "40px 20px" }}>
        {/* ABOUT */}
        <div id="about">
          <Title level={2} style={{ textAlign: "center", marginBottom: 48 }}>ABOUT US</Title>
          <Row gutter={[40, 40]} align="middle">
            <Col xs={24} md={10}>
              <Card bordered={false} style={{ borderRadius: 16, overflow: "hidden" }}>
                <motion.img src="/factory.png" alt="factory" style={{ width: "100%" }} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} />
              </Card>
            </Col>
            <Col xs={24} md={14}>
              <Paragraph style={{ fontSize: 17.5, textAlign: "justify", lineHeight: "1.8" }}>JP Hollandwoven Private Limited is a premier manufacturer specializing in heavy-duty stone storage solutions. With a commitment to engineering excellence, we transform high-quality raw materials into robust A-Frames, Bundle Racks, and Display Systems that stand the test of time.
                We produce stone storage products using high-quality raw materials and state-of-the-art technologies. We are CE EU-4493, ISO 9001:2015 certified.
              </Paragraph>
            </Col>
          </Row>
        </div>

        <Divider style={{ margin: "80px 0" }} />

        {/* PRODUCTS */}
        <div id="products">
          <Title level={2} style={{ textAlign: "center", marginBottom: 60 }}>OUR PRODUCTS</Title>
          <Row gutter={[32, 40]}>
            {products.map((item, idx) => (
              <Col xs={24} lg={8} key={idx}>
                <ProductCard product={item} />
              </Col>
            ))}
          </Row>
        </div>

        <Divider style={{ margin: "80px 0" }} />

        {/* GALLERY - Clean Custom Arrows */}
        <div id="gallery">
          <Title level={2} style={{ textAlign: "center", marginBottom: 48 }}>GALLERY</Title>

          <div style={{ position: "relative", borderRadius: 20, overflow: "hidden" }}>
            <Carousel
              ref={galleryRef}
              autoplay
              autoplaySpeed={3000}
              arrows={false}
              style={{ borderRadius: 20 }}
            >
              {galleryImages.map((img, idx) => (
                <div key={idx}>
                  <div style={{ height: isMobileOrTablet ? 320 : 620, display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f5f5" }}>
                    <Image
                      src={`/${img}.png`}
                      style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
                      preview={{ mask: <div style={{ color: "#fff" }}>Click to Zoom</div> }}
                    />
                  </div>
                </div>
              ))}
            </Carousel>

            {/* Custom Arrows */}
            <div className="gallery-arrow" style={leftArrowStyle} onClick={() => galleryRef.current?.prev()}>
              <LeftOutlined />
            </div>
            <div className="gallery-arrow" style={rightArrowStyle} onClick={() => galleryRef.current?.next()}>
              <RightOutlined />
            </div>
          </div>
        </div>

        <Divider style={{ margin: "80px 0" }} />

        {/* TESTIMONIALS */}
        <div id="reviews">
          <Title level={2} style={{ textAlign: "center", marginBottom: 48 }}>TESTIMONIALS</Title>
          <Carousel autoplay dots>
            {testimonials.map((item, i) => (
              <div key={i}>
                <Card style={{ maxWidth: 760, margin: "0 auto", textAlign: "center", padding: "30px 20px", borderRadius: 16 }}>
                  <Paragraph style={{ fontSize: 18, fontStyle: "italic", lineHeight: "1.75" }}>"{item.text}"</Paragraph>
                  <Paragraph strong style={{ marginTop: 20 }}>- {item.name}</Paragraph>
                </Card>
              </div>
            ))}
          </Carousel>
        </div>
      </div>

      {/* Global Styles for Gallery Arrows Hover */}
      <style>{`
        .gallery-arrow:hover {
          background: rgba(0,0,0,0.85) !important;
          transform: translateY(-50%) scale(1.08);
        }
      `}</style>
    </Layout>
  );
}

export default Home;